import React, {useMemo, useState} from "react";
import {
    GearCategory, GearItem, GearWeightMeasurement,
    TripDetails,
    TripGearCategory, useDeleteTripGear,
    useUpdateTripGear
} from "../../api";
import {
    Checkbox, IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import {createUseStyles} from "react-jss";
import {PackCarrySelect} from "../common/PackCarrySelect";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {TripViewCard} from "./TripViewCard";
import {SearchableList} from "../common/SearchableList";

const style = createUseStyles({
    root: {
        flexGrow: 3,
        overflowX: 'scroll',
    },
    card_root: {
        // minWidth: 650
    },
    gear_select: {
        width: 250,
    },
    pack_carry_select: {
        width: 100,
    }
});

interface TripGearListEntryProps {
    gear: TripGearCategory;
    category: GearCategory;
    gear_options: GearItem[];
    gear_weights: Record<number, GearWeightMeasurement>;
    onChange(change: TripGearCategory): void;
    onDelete(gear: TripGearCategory): void;
}

function TripGearListEntry({gear, category, gear_options, gear_weights, onChange, onDelete}: TripGearListEntryProps) {
    const classes = style();

    let secondary_text: any = category.description;
    if (gear.gear_id && gear_weights.hasOwnProperty(gear.gear_id)) {
        const item = gear_weights[gear.gear_id];
        secondary_text = <span>{item.weight} oz</span>
    }
    const actions = <div>
        <PackCarrySelect value={gear.weight_category}
                         onChange={(cat) => onChange({...gear, weight_category: cat})}
                         className={classes.pack_carry_select}/>
        <Select value={gear.gear_id ? gear.gear_id : undefined}
                className={classes.gear_select}
                onChange={e => onChange({...gear, gear_id: e.target.value as number})}>
            <MenuItem value={undefined}>None</MenuItem>
            {gear_options.map(item => {
                let weight = '';
                const found_weight = gear_weights[item.id];
                if (found_weight !== undefined) {
                    weight = ` - ${found_weight.weight}oz`;
                }
                return <MenuItem key={item.id} value={item.id}>{item.make} - {item.model}{weight}</MenuItem>;
            })}
        </Select>
        <IconButton onClick={() => onDelete(gear)}>
            <DeleteIcon/>
        </IconButton>
    </div>
    return <ListItem secondaryAction={actions}>
        <ListItemIcon onClick={() => onChange({...gear, is_packed: !gear.is_packed})}>
            <Checkbox edge="start"
                      checked={gear.is_packed && gear.gear_id !== undefined}/>
        </ListItemIcon>
        <ListItemText primary={category.name}
                      secondary={secondary_text}/>
    </ListItem>
}

interface TripGearListProps {
    trip_details: TripDetails;
    gear_categories: Record<number, GearCategory>;
    gear: Record<number, GearItem>;
    gear_weights: Record<number, GearWeightMeasurement>;
    onChange(changes: TripGearCategory[]): void;
}

export function TripGearList(props: TripGearListProps) {
    const update_trip_gear = useUpdateTripGear()[1];
    const delete_trip_gear = useDeleteTripGear();
    const [trip_category_to_delete, set_trip_category_to_delete] = useState<TripGearCategory | undefined>(undefined);
    const [is_busy_deleting, set_is_busy_deleting] = useState(false);

    function get_trip_category_name(category: TripGearCategory | undefined): string {
        if (category !== undefined) {
            if (props.gear_categories.hasOwnProperty(category.gear_category_id)) {
                return props.gear_categories[category.gear_category_id].name;
            } else {
                return "unknown";
            }
        }
        return "";
    }

    const gear_options_map = useMemo(() => {
        let selects: Record<number, GearItem[]> = {};
        for (const category of Object.values(props.gear_categories)) {
            selects[category.id] = Object.values(props.gear).filter(i => i.category_id === category.id);
        }
        return selects
    }, [props.gear, props.gear_categories]);

    function on_change_gear_list_entry(change: TripGearCategory, index: number) {
        update_trip_gear(change, () => {});
        let changes = props.trip_details.gear.slice();
        changes[index] = change;
        props.onChange(changes);
    }

    function delete_trip_gear_category(category: TripGearCategory | undefined) {
        if (category !== undefined) {
            set_is_busy_deleting(true);
            delete_trip_gear(props.trip_details.trip.id, category.id, () => {
                set_is_busy_deleting(false);
                set_trip_category_to_delete(undefined);
                props.onChange(props.trip_details.gear.filter(item => item.id !== category.id));
            });
        }
    }

    return <TripViewCard flexGrow={3} minWidth={650}>
        <SearchableList list={props.trip_details.gear}
                        render={(trip_gear, i) => {
                            const gear_category = props.gear_categories[trip_gear.gear_category_id];
                            return <TripGearListEntry key={trip_gear.id}
                                                      gear={trip_gear}
                                                      category={gear_category}
                                                      gear_options={gear_options_map[trip_gear.gear_category_id]}
                                                      gear_weights={props.gear_weights}
                                                      onChange={change => on_change_gear_list_entry(change, i)}
                                                      onDelete={g => set_trip_category_to_delete(g)}/>
                        }}
                        element_matches={(e, search) => {
                            const gear_category = props.gear_categories[e.gear_category_id];
                            return gear_category.name.toLowerCase().includes(search)
                                || gear_category.description.toLowerCase().includes(search);
                        }}/>

        <ConfirmDialog is_open={trip_category_to_delete !== undefined}
                       title={"Delete \"" + get_trip_category_name(trip_category_to_delete) + "\"?"}
                       content={"Are you sure you want to delete \"" + get_trip_category_name(trip_category_to_delete) + "\"?"}
                       onClose={() => set_trip_category_to_delete(undefined)}
                       onConfirmed={() => delete_trip_gear_category(trip_category_to_delete)}
                       is_busy={is_busy_deleting}/>
    </TripViewCard>
}